import { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import moment from "moment"
import toast from "react-hot-toast"

import { Pagination } from "components"
import Table from "components/table"
import Label from "components/forms/label"
import Input from "components/forms/input"
import SelectDescription from "components/forms/select-description"
import { HO_OUTLET_IDS } from "helpers/general-helper"

import useBranchRepositories from "repositories/branch"
import useOutletCashLedgerReportRepositories from "repositories/outlet-cash-ledger-report"

const formatNumber = (value) => Number(value ?? 0).toLocaleString("id-ID")
const formatCurrency = (value) => Number(value ?? 0).toLocaleString("id-ID")

export default function List() {
  const [searchParams, setSearchParams] = useSearchParams()

  const outletId = searchParams.get("outlet_id") || "ALL"
  const month = searchParams.get("month") || moment().format("YYYY-MM")
  const productSearch = searchParams.get("product_search") || ""
  const page = searchParams.get("page") || "1"
  const perPage = searchParams.get("per_page") || "20"
  const [productSearchInput, setProductSearchInput] = useState(productSearch)

  useEffect(() => {
    setProductSearchInput(productSearch)
  }, [productSearch])

  const submitProductSearch = () => {
    if (productSearchInput === productSearch) return
    const nextParams = new URLSearchParams(searchParams)
    if (productSearchInput.trim() === "") nextParams.delete("product_search")
    else nextParams.set("product_search", productSearchInput.trim())
    nextParams.set("page", "1")
    setSearchParams(nextParams)
  }

  const { data: branchSelection, isLoading: isLoadingBranchSelection } =
    useBranchRepositories({ paginate: false, excludeYoiboi: true })

  const branchOptions = useMemo(() => {
    const branches = Array.isArray(branchSelection) ? branchSelection : []

    // 290726 (permintaan user): opsi grup "HO" ditambahkan di samping "Semua Outlet",
    // menyamai filter di laporan Sales Qty Setahun. Nilainya CSV id (HO_OUTLET_IDS =
    // 1 & 15) — backend laporan ini sudah menerima CSV (explode + whereIn), jadi
    // tidak perlu penanganan khusus seperti sentinel "ALL".
    // Hanya ditampilkan bila outlet HO memang ada di daftar yang boleh diakses user.
    const hoIds = HO_OUTLET_IDS.filter((id) => branches.some((b) => Number(b?.id) === id))
    const hoOption = hoIds.length
      ? [{
          id: hoIds.join(","),
          name: "HO",
          description: "PT Atalla + Gudang Titip Pabrik",
        }]
      : []

    return [
      {
        id: "ALL",
        name: "Semua Outlet",
        description: "Gabungan seluruh outlet",
      },
      ...hoOption,
      ...branches,
    ]
  }, [branchSelection])

  const {
    data: reportData,
    isLoading,
  } = useOutletCashLedgerReportRepositories({
    outlet_id: outletId || undefined,
    month: month || undefined,
    product_search: productSearch || undefined,
    page,
    per_page: perPage,
    enabled: !!month,
  })

  const items = useMemo(() => reportData?.items?.data ?? [], [reportData])

  const mergeSearchParams = (nextValues) => {
    const nextParams = new URLSearchParams(searchParams)

    Object.entries(nextValues).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        nextParams.delete(key)
      } else {
        nextParams.set(key, value)
      }
    })

    setSearchParams(nextParams)
  }

  const handleExport = () => {
    if (!month) {
      toast.error("Pilih bulan terlebih dahulu")
      return
    }

    const qs = new URLSearchParams({
      outlet_id: outletId,
      month,
    })

    if (productSearch) {
      qs.set("product_search", productSearch)
    }

    window.open(
      `${process.env.REACT_APP_INVENTORY_API_URL}/reports/end-of-month/export?${qs.toString()}`,
      "_blank"
    )
  }

  const dataListHeaders = [
    { isAction: false, content: "Grup Produk" },
    { isAction: false, content: "Produk" },
    // { isAction: false, content: "SKU" },
    { isAction: false, content: "Stok Awal" },
    { isAction: false, content: "Stok Masuk" },
    { isAction: false, content: "Stok Keluar" },
    { isAction: false, content: "Pergerakan Bersih" },
    { isAction: false, content: "Stok Akhir" },
  ]

  const dataListColumns = [
    { type: "default", bold: true, content: (v) => <div className="w-[200px] whitespace-normal break-words leading-5">
          {v.product_group || "-"}
        </div> },
    { type: "default", content: (v) => <div className="w-[200px] whitespace-normal break-words leading-5">
          {v.product_name || "-"}
        </div> },
    // { type: "default", content: (v) => v.product_sku || "-" },
    { type: "default", content: (v) => <span className="font-semibold text-sky-700">{formatNumber(v.opening_stock)}</span> },
    { type: "default", content: (v) => <span className="font-semibold text-green-700">{formatNumber(v.stock_in)}</span> },
    { type: "default", content: (v) => <span className="font-semibold text-red-700">{formatNumber(v.stock_out)}</span> },
    {
      type: "default",
      content: (v) => (
        <span className={`font-semibold ${Number(v.net_movement ?? 0) >= 0 ? "text-amber-700" : "text-red-700"}`}>
          {formatNumber(v.net_movement)}
        </span>
      ),
    },
    { type: "default", content: (v) => <span className="font-semibold text-neutral-800">{formatNumber(v.ending_stock)}</span> },
  ]

  return (
    <div className="space-y-6">
      <div className="p-5 bg-white border rounded-2xl border-neutral-200">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">Akhir Bulan</h2>
            <p className="mt-1 text-sm text-neutral-500">
              Penutupan bulanan untuk melihat ringkasan penjualan dan rekap stock movement per produk.
            </p>
          </div>

          <div className="flex items-end gap-3 flex-wrap">
            <div className="w-56">
              <Label value="Outlet" />
              <SelectDescription
                selection={branchOptions}
                isLoading={isLoadingBranchSelection}
                value={outletId}
                onChange={(value) => {
                  mergeSearchParams({ outlet_id: value || "ALL", page: "1" })
                }}
                keyValue={(v) => v.id}
                title={(v) => v.name}
                description={(v) => v.code || v.id}
                placeholder="Pilih Outlet..."
              />
            </div>

            <div className="w-44">
              <Label value="Bulan" />
              <Input
                type="month"
                value={month}
                onChange={(e) => {
                  mergeSearchParams({ month: e.target.value, page: "1" })
                }}
              />
            </div>

            <div className="w-72">
              <Label value="Cari Produk" />
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={productSearchInput}
                  placeholder="Nama produk / SKU..."
                  onChange={(e) => setProductSearchInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitProductSearch() } }}
                />
                <button
                  type="button"
                  onClick={submitProductSearch}
                  className="px-3 py-2 text-sm font-semibold text-white rounded-lg bg-brand-900 hover:bg-brand-800"
                >
                  Cari
                </button>
              </div>
            </div>

            <div className="w-32">
              <Label value="Per Halaman" />
              <select
                value={perPage}
                onChange={(e) => {
                  mergeSearchParams({ per_page: e.target.value, page: "1" })
                }}
                className="w-full px-3 py-3 text-sm border rounded-xl border-neutral-200 focus:outline-none focus:ring"
              >
                {[10, 20, 50, 100].map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </div>

            <button
              type="button"
              onClick={handleExport}
              className="inline-flex items-center px-4 py-3 text-sm font-medium text-white transition bg-emerald-600 rounded-xl"
            >
              Ekspor Excel
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-7">
        {[
          { label: "Cabang", value: reportData?.branch_name || "-", tone: "text-neutral-900" },
          { label: "Total Order", value: formatNumber(reportData?.summary?.total_orders), tone: "text-blue-700" },
          { label: "Total Penjualan", value: formatCurrency(reportData?.summary?.total_sales), tone: "text-emerald-700" },
          { label: "Qty Terjual", value: formatNumber(reportData?.summary?.total_qty_sold), tone: "text-indigo-700" },
          { label: "Stok Awal", value: formatNumber(reportData?.summary?.opening_stock), tone: "text-sky-700" },
          { label: "Stok Masuk", value: formatNumber(reportData?.summary?.stock_in), tone: "text-green-700" },
          { label: "Stok Keluar", value: formatNumber(reportData?.summary?.stock_out), tone: "text-red-700" },
        ].map((card) => (
          <div key={card.label} className="p-4 border rounded-xl border-neutral-200 bg-neutral-50">
            <div className="text-xs uppercase tracking-wide text-neutral-500">{card.label}</div>
            <div className={`mt-2 text-2xl font-semibold ${card.tone}`}>
              {isLoading ? "..." : card.value}
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4">
        <div className="p-5 bg-white border rounded-2xl border-neutral-200">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-neutral-900">Rincian Sumber Penjualan</h3>
              <p className="mt-1 text-xs text-neutral-500">
                Kontribusi data penjualan dari Atalla inventory.
              </p>
            </div>
            <span className="px-2.5 py-1 text-xs font-medium text-blue-700 rounded-full bg-blue-50 border border-blue-200">
              ATALLA
            </span>
          </div>

          <div className="grid grid-cols-1 gap-3 mt-4 sm:grid-cols-3">
            {[
              { label: "Order", value: formatNumber(reportData?.summary?.atalla_total_orders), tone: "text-blue-700" },
              { label: "Penjualan", value: formatCurrency(reportData?.summary?.atalla_total_sales), tone: "text-emerald-700" },
              { label: "Qty Terjual", value: formatNumber(reportData?.summary?.atalla_total_qty_sold), tone: "text-indigo-700" },
            ].map((card) => (
              <div key={card.label} className="p-4 rounded-xl bg-neutral-50 border border-neutral-200">
                <div className="text-xs uppercase tracking-wide text-neutral-500">{card.label}</div>
                <div className={`mt-2 text-xl font-semibold ${card.tone}`}>
                  {isLoading ? "..." : card.value}
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>

      <div className="p-5 bg-white border rounded-2xl border-neutral-200">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">Rekap Pergerakan Stok</h2>
            <p className="mt-1 text-sm text-neutral-500">
              Rekap stok awal, stok masuk, dan stok keluar produk selama bulan yang dipilih.
            </p>
          </div>

          <div className="text-sm text-neutral-500">
            Periode: <span className="font-medium text-neutral-800">{month || "-"}</span>
          </div>
        </div>

        <div className="mt-5">
          <Table
            isLoading={isLoading}
            data={items}
            headers={dataListHeaders}
            columns={dataListColumns}
            isEmpty={!items.length}
            isEmptyOnSearching={false}
          />
          <div className="mt-4">
            <Pagination
              links={reportData?.items?.links}
              from={reportData?.items?.from}
              to={reportData?.items?.to}
              total={reportData?.items?.total}
            />
          </div>
        </div>
      </div>
    </div>
  )
}